import { HttpClient, HttpResourceRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { TranslationOverride, TranslationOverrideSaveRequest } from '@raptor-fe/core';
import { Observable } from 'rxjs';
import { ServiceUtils } from '../service-utils/service-utils';

/**
 * Reads and writes a tenant's translation overrides. Reads are exposed as `httpResource` request
 * builders so the owning store tracks them as state; writes stay one-shot. Saves are per key rather
 * than a whole-page submit so two administrators editing different labels cannot overwrite each
 * other, and clearing is a delete so the key falls back to the product default rather than to a
 * stored copy of it.
 */
@Injectable({
    providedIn: 'root',
})
export class TranslationOverrideApiService {
    private readonly http = inject(HttpClient);
    private readonly serviceUtils = inject(ServiceUtils);

    /**
     * Builds the request listing overrides for one language.
     *
     * @param tenantId - The tenant identifier
     * @param language - The language code, e.g. `en-AU`
     * @param branchId - When supplied, that branch's overrides are returned alongside the tenant-level ones
     * @returns The request, or undefined until tenant and language are known so the resource stays idle
     */
    overridesRequest(tenantId: string, language: string, branchId?: string | null): HttpResourceRequest | undefined {
        if (!tenantId || !language) {
            return undefined;
        }
        return { url: this.baseUrl(tenantId), params: branchId ? { language, branchId } : { language } };
    }

    /**
     * Builds the request listing the product-shipped defaults for one language, so the screen can
     * show what clearing an override reverts to.
     *
     * @param tenantId - The tenant identifier
     * @param language - The language code, e.g. `en-AU`
     * @returns The request, or undefined until tenant and language are known so the resource stays idle
     */
    defaultsRequest(tenantId: string, language: string): HttpResourceRequest | undefined {
        if (!tenantId || !language) {
            return undefined;
        }
        return { url: `${this.baseUrl(tenantId)}/defaults`, params: { language } };
    }

    /**
     * Builds the request listing the languages the deployment supports, which decides the default
     * editing language and whether a language picker is needed at all.
     *
     * @returns The request
     */
    supportedLanguagesRequest(): HttpResourceRequest {
        return { url: this.serviceUtils.getBaseUrl('language') };
    }

    /**
     * Creates or replaces the override for one key at the requested scope.
     *
     * @param tenantId - The tenant identifier
     * @param request - The scope, language, composed key and replacement text
     * @returns The persisted override
     */
    save(tenantId: string, request: TranslationOverrideSaveRequest): Observable<TranslationOverride> {
        return this.http.put<TranslationOverride>(this.baseUrl(tenantId), request);
    }

    /**
     * Removes an override so the key reverts to the next value in the resolution order.
     *
     * @param tenantId - The tenant identifier
     * @param id - The override identifier
     * @returns The empty removal response
     */
    delete(tenantId: string, id: string): Observable<void> {
        return this.http.delete<void>(`${this.baseUrl(tenantId)}/${id}`);
    }

    private baseUrl(tenantId: string): string {
        return this.serviceUtils.getBaseUrl('translation-override', tenantId);
    }
}
